import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { SignOut, Timer } from '@phosphor-icons/react';

const KioskTimekeeper = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [show, setShow] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [entries, setEntries] = useState([]);
  const [timeInputs, setTimeInputs] = useState({});
  const [liveData, setLiveData] = useState({ current: null, next_up: [] });

  useEffect(() => {
    const s = sessionStorage.getItem('kiosk_session');
    if (!s) { navigate('/kiosk'); return; }
    const parsed = JSON.parse(s);
    if (parsed.role !== 'timekeeper') { navigate('/kiosk'); return; }
    setSession(parsed);
    setShow(parsed.show);
    if (parsed.show?.classes?.length > 0) setSelectedClassId(parsed.show.classes[0].id);
  }, [navigate]);

  const loadEntries = useCallback(async () => {
    if (!session || !selectedClassId) return;
    try {
      const { data } = await api.get(`/shows/${session.show_id}/classes/${selectedClassId}/entries`);
      setEntries(data);
      const { data: live } = await api.get(`/results/${selectedClassId}/live`);
      setLiveData(live);
    } catch {}
  }, [session, selectedClassId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);
  useEffect(() => {
    if (!selectedClassId) return;
    const interval = setInterval(loadEntries, 10000);
    return () => clearInterval(interval);
  }, [selectedClassId, loadEntries]);

  const handleSubmitTime = async (entryId) => {
    const val = (timeInputs[entryId] || '').trim();
    if (!val) return;
    const scrMarkers = ['scr', 'scratch'];
    const ntMarkers = ['nt', 'no time', 'dns', 'dnf', 'to'];
    let time = null;
    let status = 'COMPLETED';
    if (scrMarkers.includes(val.toLowerCase())) { status = 'SCR'; }
    else if (ntMarkers.includes(val.toLowerCase())) { status = 'NT'; }
    else { time = parseFloat(val); if (isNaN(time) || time <= 0) return; }

    try {
      await api.post('/scores', { entry_id: entryId, class_id: selectedClassId, time, knocks: 0, status });
      toast.success(status !== 'COMPLETED' ? `${status} recorded` : `${time.toFixed(3)} recorded`);
      setTimeInputs(prev => ({ ...prev, [entryId]: '' }));
      loadEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to submit'); }
  };

  if (!session || !show) return null;

  // v248 — Trust backend order (respects class-level draw_reversed).
  const sorted = [...entries].sort((a, b) => (a.draw_number == null) - (b.draw_number == null));

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#161616] border-b border-[#262626] px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div>
          <p className="text-white font-bold text-sm" style={{fontFamily: 'Outfit'}}>{show.name}</p>
          <p className="text-[#34C759] text-xs font-semibold flex items-center gap-1"><Timer size={12} /> Timekeeper</p>
        </div>
        <button onClick={() => { sessionStorage.removeItem('kiosk_session'); navigate('/kiosk'); }} className="flex items-center gap-1 text-[#9E9E9E] hover:text-white text-sm">
          <SignOut size={16} /> Exit
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-4">
        {/* Class Selector */}
        <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}
          className="w-full px-4 py-3 bg-[#161616] border border-[#262626] rounded-lg text-white mb-4 focus:outline-none focus:border-[#34C759]"
          data-testid="tk-class-select">
          {show.classes.map(c => <option key={c.id} value={c.id}>{c.name}{c.scheduled_day ? ` (${c.scheduled_day})` : ''}</option>)}
        </select>

        {/* Now Running */}
        {liveData.current && (
          <div className="bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg p-4 mb-4 text-center">
            <p className="text-[#34C759] text-xs font-semibold uppercase">Now Running — Draw #{liveData.current.draw_number}</p>
            <p className="text-2xl font-black text-white" style={{fontFamily: 'Outfit'}}>{liveData.current.rider_name}</p>
            {liveData.current.horse_name && <p className="text-[#9E9E9E] text-sm">{liveData.current.horse_name}</p>}
          </div>
        )}

        {/* Entry List with Time Inputs */}
        <div className="space-y-2" data-testid="tk-entry-list">
          {sorted.map(entry => {
            const hasScore = entry.score && entry.score.final_time != null;
            const isNTSCR = entry.score && ['NT', 'SCR', 'SCRATCH'].includes(entry.score.status);
            return (
              <div key={entry.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${hasScore || isNTSCR ? 'bg-[#161616] border-[#262626] opacity-60' : 'bg-[#161616] border-[#34C759]/30'}`}
                data-testid={`tk-entry-${entry.id}`}>
                <span className="text-[#0096FF] text-sm font-bold w-8 flex-shrink-0">#{entry.draw_number || '-'}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{entry.rider_name}</p>
                  {entry.horse_name && <p className="text-[#9E9E9E] text-xs truncate">{entry.horse_name}</p>}
                </div>
                {hasScore || isNTSCR ? (
                  <span className="text-[#34C759] text-sm font-bold flex-shrink-0" style={{fontFamily: 'JetBrains Mono'}}>
                    {isNTSCR ? entry.score.status : entry.score.final_time.toFixed(3)}
                  </span>
                ) : (
                  <div className="flex gap-1 flex-shrink-0">
                    <input type="text" value={timeInputs[entry.id] || ''}
                      onChange={(e) => setTimeInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === 'Enter' && handleSubmitTime(entry.id)}
                      placeholder="Time" inputMode="decimal"
                      className="w-24 px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm text-center focus:outline-none focus:border-[#34C759]"
                      data-testid={`tk-time-${entry.id}`} />
                    <button onClick={() => handleSubmitTime(entry.id)}
                      className="px-3 py-1.5 bg-[#34C759] text-white text-xs font-bold rounded hover:bg-[#34C759]/80"
                      data-testid={`tk-submit-${entry.id}`}>
                      Go
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default KioskTimekeeper;
